import { Service, signal } from '@angular/core';

/** Where the raw per-install identifier is kept, so it survives reloads and updates. */
export const PUSH_UNIQUE_DEVICE_ID_STORAGE_KEY = 'push-unique-device-id';

/**
 * The stable identifier kala sends as `x-device-id`.
 *
 * The raw value — the push unique device id — is generated once per install and
 * kept in localStorage, so the same browser keeps the same identity across
 * sessions. It is never sent as-is: every request carries its SHA-256 digest,
 * which is what the backend matches on. Hashing is one-way, so a leaked header
 * cannot be replayed back into the push identifier it was derived from.
 *
 * `crypto.subtle` is async and only exists in a secure context, so the digest is
 * computed once at startup (see `provideDeviceId`) and read synchronously from
 * there on. A device that cannot hash — an insecure origin, an ancient engine —
 * resolves to `null` and simply sends no `x-device-id`, which is better than
 * sending the raw id or blocking every request.
 */
@Service()
export class DeviceIdService {
	private readonly hashed = signal<string | null>(null);

	/** The SHA-256 digest to send, or `null` until {@link resolve} has run. */
	readonly deviceId = this.hashed.asReadonly();

	/** Reads — or creates — the push unique device id this install is known by. */
	rawDeviceId(): string {
		const stored = readStoredId();
		if (stored) {
			return stored;
		}

		const created = createId();
		writeStoredId(created);

		return created;
	}

	/**
	 * Computes the digest so {@link deviceId} can be read synchronously afterwards.
	 * Safe to call more than once; the work is done on the first call only.
	 */
	async resolve(): Promise<string | null> {
		if (this.hashed()) {
			return this.hashed();
		}

		const digest = await sha256Hex(this.rawDeviceId());
		this.hashed.set(digest);
		return digest;
	}
}

function readStoredId(): string | null {
	try {
		return localStorage.getItem(PUSH_UNIQUE_DEVICE_ID_STORAGE_KEY);
	} catch {
		// Private mode and blocked storage both throw; a fresh id per session is
		// still better than no identity at all.
		return null;
	}
}

function writeStoredId(id: string): void {
	try {
		localStorage.setItem(PUSH_UNIQUE_DEVICE_ID_STORAGE_KEY, id);
	} catch {
		// See readStoredId — nothing to do but carry on with the in-memory value.
	}
}

function createId(): string {
	if (typeof crypto?.randomUUID === 'function') {
		return crypto.randomUUID();
	}

	// Non-secure origins expose `crypto` without `randomUUID`; the id only has to
	// be unique per install, not unguessable, so this is an acceptable fallback.
	return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

/** @returns the lowercase hex SHA-256 of `value`, or `null` where WebCrypto is unavailable. */
async function sha256Hex(value: string): Promise<string | null> {
	if (!crypto?.subtle) {
		return null;
	}

	try {
		const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));

		return Array.from(new Uint8Array(digest))
			.map(byte => byte.toString(16).padStart(2, '0'))
			.join('');
	} catch {
		return null;
	}
}
